// Pure live-activity reconciliation shared by private chat and Task threads. The server remains the
// activity authority; this utility only folds started/settled previews by their stable invocation identity.

import type { ChatActivityItem } from './projections.ts';

/**
 * A durable settled row replaces the oldest matching running call while retaining its toolCallId, so the
 * structured renderer updates one card in place. Replayed durable ids are idempotent; repeated same-tool
 * calls retain observation order.
 */
export function reconcileLiveActivity(
  current: readonly ChatActivityItem[],
  incoming: ChatActivityItem,
): readonly ChatActivityItem[] {
  const exact = current.findIndex((row) => row.id === incoming.id);
  if (exact >= 0) {
    return current.map((row, index) => (index === exact ? { ...row, ...incoming } : row));
  }
  if (incoming.outcome !== 'running') {
    const pending = current.findIndex((row) => isMatchingRunningActivity(row, incoming));
    if (pending >= 0) {
      const running = current[pending]!;
      const settled: ChatActivityItem = {
        ...incoming,
        toolCallId: incoming.toolCallId ?? running.toolCallId ?? running.id,
        ...(incoming.toolName !== undefined
          ? { toolName: incoming.toolName }
          : running.toolName !== undefined
            ? { toolName: running.toolName }
            : {}),
      };
      return current.map((row, index) => (index === pending ? settled : row));
    }
  }
  return [...current, incoming];
}

function isMatchingRunningActivity(running: ChatActivityItem, settled: ChatActivityItem): boolean {
  if (running.outcome !== 'running') return false;
  if (settled.toolCallId !== undefined) return running.toolCallId === settled.toolCallId;
  if (settled.toolName !== undefined) return running.toolName === settled.toolName;
  return running.kind === settled.kind && running.label === settled.label;
}
